import { DEFAULT_CURRENCY } from '@/data/config/DEFAULTS';
import { GA4_EVENT_SELECT_ITEM, NOT_APPLICABLE } from '@/data/constants/gtm';
import { TYPES } from '@/data/constants/product';
import { dFix } from '@/data/utils/floatingPoint';
import { productIsA } from '@/utils/productIsA';
import { GA4EcommerceEventPayload } from 'presentation/events/types/ga4Types';
import {
	BuildGA4EcommerceEventItemPayloadProps,
	buildGA4EcommerceEventItemPayload,
} from 'presentation/events/utils/ga4Utils';

export const buildGA4SelectItemEventPayload = ({
	physicalStoreId,
	settings,
	product,
	categories,
	quantity,
	isEmarsys,
	promotionDetails,
	plpBadges,
}: BuildGA4EcommerceEventItemPayloadProps): GA4EcommerceEventPayload => {
	const bundleId = productIsA(product, TYPES.bundle) ? product.partNumber : undefined;

	const calculateBundlePrice = () => {
		if (!bundleId || !product.components || product.components.length === 0) {
			return 0;
		}

		const prices = product.components
			.map((c) => c.productPrice?.offer)
			.filter((p): p is number => p !== undefined && p !== null);

		if (prices.length > 0) {
			const price = (Math.min(...prices) + Math.max(...prices)) / 2;

			// Round price to 2 decimals
			return Math.round(price * 100) / 100;
		}

		return 0;
	};

	const items = [
		buildGA4EcommerceEventItemPayload({
			isEmarsys,
			bundleId,
			product,
			categories,
			physicalStoreId,
			settings,
			quantity,
			promotionDetails,
			plpBadges,
		}),
	];

	const updatedItems = items.map((item) => {
		const promotion_id = isEmarsys ? NOT_APPLICABLE : item.promotion_id;
		const promotion_name = isEmarsys ? NOT_APPLICABLE : item.promotion_name;

		if (bundleId) {
			return {
				...item,
				price: calculateBundlePrice(),
				promotion_name,
				promotion_id,
			};
		}

		return {
			...item,
			promotion_name,
			promotion_id,
		};
	});

	const value = dFix(
		updatedItems.reduce((value, item) => {
			const price = item.price ?? 0;
			const quantity = item.quantity ?? 0;

			return value + price * quantity;
		}, 0),
		2
	);

	const payload: GA4EcommerceEventPayload = {
		event: GA4_EVENT_SELECT_ITEM,
		ecommerce: {
			currency: DEFAULT_CURRENCY,
			value,
			items: updatedItems,
		},
	};

	if (bundleId) {
		payload.ecommerce.bundle_id = bundleId;
	}

	return payload;
};
